// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Daily Catch  (live data for pages/DailyCatch.tsx)
// Keeps the page in step with what staff publish: fetches /api/public/drop now, then every POLL_MS while the tab is
// visible, and again the moment the tab becomes visible or the network returns. A failed refresh keeps the last good
// data and marks it `stale` (so a bad connection during a rush doesn't blank the page). `now()` is the server's clock
// (device clock + the offset measured on each reply), so the dispatch countdown is right even on a wrong-clock phone.
// Polling is deliberately simple: the API answers from a 5 s shared cache, so a crowd costs the backend very little.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchDrop, type DropView } from "../utils/drop";

export const POLL_MS = 10_000;

interface State {
  data: DropView | null;
  /** Milliseconds to add to Date.now() to get server time. */
  offsetMs: number;
  /** Last successful fetch (device clock), for "updated Ns ago". */
  updatedAt: number | null;
  /** The latest refresh failed; `data` (if any) is the last good copy. */
  stale: boolean;
  loading: boolean;
}

export default function useDrop() {
  const [state, setState] = useState<State>({ data: null, offsetMs: 0, updatedAt: null, stale: false, loading: true });
  const inFlight = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    try {
      const data = await fetchDrop(controller.signal);
      const received = Date.now();
      setState({ data, offsetMs: new Date(data.serverTime).getTime() - received, updatedAt: received, stale: false, loading: false });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setState((current) => ({ ...current, stale: true, loading: false }));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const tick = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const timer = window.setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("online", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("online", tick);
      inFlight.current?.abort();
    };
  }, [refresh]);

  return { ...state, refresh, now: () => Date.now() + state.offsetMs };
}
